import { useState, useEffect } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useLanguage } from '@/i18n/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Shield, Users, TrendingUp, RefreshCw, Download, BarChart3, Activity } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import { FOUNDER_CONFIG } from '@/types/founder';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';

interface FounderRecord {
  user_id: string;
  founder_status: string;
  slot_number: number;
  qualified_at: string;
  expired_at: string | null;
  last_out_of_announcements_date: string | null;
  announcement_credits: number | null;
  has_purchased_bundle: boolean | null;
  highest_bundle_purchased: string | null;
  total_announcements: number;
  total_reloads: number;
  last_reload_at: string | null;
  total_credits_purchased: number | null;
  created_at: string;
}

export default function AdminFounders() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [founders, setFounders] = useState<FounderRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  useEffect(() => {
    if (isAdmin) fetchFounders();
  }, [isAdmin]);

  const checkAdminStatus = async () => {
    if (!user) { setIsAdmin(false); return; }
    try {
      const { data, error } = await supabase.rpc('has_role', { _user_id: user.id, _role: 'admin' });
      if (error) throw error;
      setIsAdmin(data);
    } catch { setIsAdmin(false); }
  };

  const fetchFounders = async () => {
    try {
      const { data, error } = await supabase
        .from('founder_analytics' as any)
        .select('*')
        .order('slot_number', { ascending: true });
      if (error) throw error;
      setFounders((data || []) as unknown as FounderRecord[]);
    } catch (err) {
      console.error('Error fetching founders:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchFounders();
    setIsRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleExportCsv = () => {
    if (founders.length === 0) return;
    const headers = ['Slot', 'Status', 'Qualified At', 'Announcements', 'Reloads', 'Credits Purchased', 'Last Reload'];
    const csvContent = [
      headers.join(','),
      ...founders.map((f) =>
        [
          f.slot_number,
          f.founder_status,
          format(new Date(f.qualified_at), 'yyyy-MM-dd'),
          f.total_announcements,
          f.total_reloads,
          f.total_credits_purchased ?? 0,
          f.last_reload_at ? format(new Date(f.last_reload_at), 'yyyy-MM-dd') : 'N/A',
        ].join(',')
      ),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `founders-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('CSV exported');
  };

  if (!user) return <Navigate to="/login" replace />;
  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center py-20">
          <Card className="max-w-md text-center">
            <CardHeader>
              <Shield className="h-12 w-12 text-destructive mx-auto mb-4" />
              <CardTitle>Access Denied</CardTitle>
              <CardDescription>You must be an admin to view this page.</CardDescription>
            </CardHeader>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Computed metrics
  const totalFounders = founders.length;
  const activeFounders = founders.filter((f) => f.founder_status === 'active').length;
  const expiredFounders = founders.filter((f) => f.founder_status === 'expired').length;
  const avgAnnouncementsPerFounder = totalFounders > 0
    ? (founders.reduce((sum, f) => sum + f.total_announcements, 0) / totalFounders).toFixed(1)
    : '0';
  const totalFounderRevenue = founders.reduce((sum, f) => sum + (f.total_reloads || 0) * FOUNDER_CONFIG.phase1Price, 0);
  const repeatReloaders = founders.filter((f) => f.total_reloads > 1).length;
  const retentionRate = totalFounders > 0 ? ((repeatReloaders / totalFounders) * 100).toFixed(0) : '0';

  const statusData = [
    { name: 'Active', value: activeFounders, color: 'hsl(var(--primary))' },
    { name: 'Expired', value: expiredFounders, color: 'hsl(var(--muted-foreground))' },
    { name: 'Available', value: FOUNDER_CONFIG.maxSlots - totalFounders, color: 'hsl(var(--accent))' },
  ];

  // Monthly activity (group by month)
  const monthlyData = founders.reduce((acc, f) => {
    const month = format(new Date(f.qualified_at), 'yyyy-MM');
    const existing = acc.find((m) => m.month === month);
    if (existing) {
      existing.count++;
    } else {
      acc.push({ month, count: 1 });
    }
    return acc;
  }, [] as { month: string; count: number }[]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 py-12">
        <div className="container max-w-6xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
                <Shield className="h-8 w-8 text-primary" />
                Founders Analytics
              </h1>
              <p className="text-muted-foreground">Founding Member metrics and cohort analysis</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={founders.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="grid gap-4 md:grid-cols-4 mb-8">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28" />)}
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className="grid gap-4 md:grid-cols-4 mb-8">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground">Total Founders</p>
                        <p className="text-2xl font-bold">{totalFounders} <span className="text-sm text-muted-foreground">/ {FOUNDER_CONFIG.maxSlots}</span></p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Activity className="h-5 w-5 text-green-500" />
                      <div>
                        <p className="text-xs text-muted-foreground">Active / Expired</p>
                        <p className="text-2xl font-bold">{activeFounders} <span className="text-sm text-muted-foreground">/ {expiredFounders}</span></p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="h-5 w-5 text-amber-500" />
                      <div>
                        <p className="text-xs text-muted-foreground">Reload Retention</p>
                        <p className="text-2xl font-bold">{retentionRate}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground">Avg Announcements</p>
                        <p className="text-2xl font-bold">{avgAnnouncementsPerFounder}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid gap-6 md:grid-cols-2 mb-8">
                {/* Status Pie Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Founder Slots Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={statusData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {statusData.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Monthly Signup Line Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Founder Signups Over Time</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Founders Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    All Founding Members
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {founders.length === 0 ? (
                    <div className="text-center py-12">
                      <Shield className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                      <h3 className="text-lg font-medium">No Founding Members yet</h3>
                      <p className="text-sm text-muted-foreground">Founders will appear here once they qualify.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Slot</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Qualified</TableHead>
                            <TableHead>Announcements</TableHead>
                            <TableHead>Reloads</TableHead>
                            <TableHead>Credits Purchased</TableHead>
                            <TableHead>Last Reload</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {founders.map((f) => (
                            <TableRow key={f.user_id}>
                              <TableCell className="font-medium">#{f.slot_number}</TableCell>
                              <TableCell>
                                <Badge variant={f.founder_status === 'active' ? 'default' : 'secondary'}>
                                  {f.founder_status}
                                </Badge>
                              </TableCell>
                              <TableCell>{format(new Date(f.qualified_at), 'MMM d, yyyy')}</TableCell>
                              <TableCell>{f.total_announcements}</TableCell>
                              <TableCell>{f.total_reloads}</TableCell>
                              <TableCell>{f.total_credits_purchased ?? 0}</TableCell>
                              <TableCell>
                                {f.last_reload_at
                                  ? format(new Date(f.last_reload_at), 'MMM d, yyyy')
                                  : '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
